import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, Close as CloseIcon } from "@mui/icons-material";
import { AdminPageContainer, StaticCard } from "../../components/admin/sharedStyles";
import PageHeader from "../../components/PageHeader";
import { disablePluginClient, enablePluginClient, getPluginAdminCatalog, getPluginAvailableClients, getPluginClients } from "../../redux/ducks/pluginAdmin";
import { showAlert } from "../../redux/slices/alerts";
import plugins from "../../clientPlugins";

const LoadingRow = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const EmptyState = styled(Box)(({ theme }) => ({
    textAlign: "center",
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const ClickableRow = styled(TableRow)({
    cursor: "pointer",
});

const SectionTitle = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(0.5),
}));

const SectionHelper = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const EnabledList = styled(List)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    "& .MuiListItem-root + .MuiListItem-root": {
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}));

const ClientName = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
}));

const AddRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        alignItems: "stretch",
    },
}));

const ClientPicker = styled(Autocomplete)({
    flex: 1,
    maxWidth: 480,
});

const AddButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const nameCollator = new Intl.Collator("en", { sensitivity: "accent" });
const compareNames = (a, b) => nameCollator.compare(a.name, b.name);

const clientCountLabel = (count) => {
    if (count === undefined) return <CircularProgress size={16} />;
    if (count === null) return "Couldn't load";
    if (count === 0) return "No clients";
    return `${count} client${count === 1 ? "" : "s"}`;
};

const PluginList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [catalog, setCatalog] = useState(undefined);
    const [counts, setCounts] = useState({});

    useEffect(() => {
        let active = true;
        dispatch(
            getPluginAdminCatalog((data) => {
                if (!active) return;
                const list = Array.isArray(data) ? data : null;
                setCatalog(list);
                (list || []).forEach((plugin) => {
                    dispatch(
                        getPluginClients(plugin.id, (clients) => {
                            if (!active) return;
                            setCounts((prev) => ({ ...prev, [plugin.id]: Array.isArray(clients) ? clients.filter((client) => !client.isDeleted).length : null }));
                        }),
                    );
                });
            }),
        );
        return () => {
            active = false;
        };
    }, [dispatch]);

    return (
        <AdminPageContainer>
            <PageHeader title="Plugins" subtitle="Extra features that are turned on for specific clients. Open a plugin to choose its clients and settings." />
            {catalog === undefined ? (
                <LoadingRow>
                    <CircularProgress />
                </LoadingRow>
            ) : catalog === null ? (
                <EmptyState>
                    <Typography color="error">The plugin list could not be loaded. Refresh the page to try again.</Typography>
                </EmptyState>
            ) : catalog.length === 0 ? (
                <EmptyState>
                    <Typography color="text.secondary">No plugins are installed.</Typography>
                </EmptyState>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <strong>Plugin</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Enabled for</strong>
                                </TableCell>
                                <TableCell align="right">
                                    <strong>Actions</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {catalog.map((plugin) => (
                                <ClickableRow key={plugin.id} hover onClick={() => navigate(`/admin/plugins/${encodeURIComponent(plugin.id)}`)}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            {plugin.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{clientCountLabel(counts[plugin.id])}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small">Manage</Button>
                                    </TableCell>
                                </ClickableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </AdminPageContainer>
    );
};

const PluginDetail = ({ pluginId }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [plugin, setPlugin] = useState(undefined);
    const [enabled, setEnabled] = useState(undefined);
    const [available, setAvailable] = useState(undefined);
    const [picked, setPicked] = useState(null);
    const [saving, setSaving] = useState(false);
    const manifest = plugins.find((entry) => entry.id === pluginId);

    const activeRef = useRef(true);

    useEffect(() => {
        activeRef.current = true;
        return () => {
            activeRef.current = false;
        };
    }, []);

    const availableRequestRef = useRef(0);

    const loadAvailable = useCallback(() => {
        availableRequestRef.current += 1;
        const requestId = availableRequestRef.current;
        dispatch(
            getPluginAvailableClients(pluginId, (clients) => {
                if (activeRef.current && requestId === availableRequestRef.current) setAvailable(Array.isArray(clients) ? clients : null);
            }),
        );
    }, [dispatch, pluginId]);

    const enabledRequestRef = useRef(0);

    const loadEnabled = useCallback(() => {
        enabledRequestRef.current += 1;
        const requestId = enabledRequestRef.current;
        dispatch(
            getPluginClients(pluginId, (clients) => {
                if (activeRef.current && requestId === enabledRequestRef.current) setEnabled(Array.isArray(clients) ? clients : null);
            }),
        );
    }, [dispatch, pluginId]);

    useEffect(() => {
        let active = true;
        dispatch(
            getPluginAdminCatalog((data) => {
                if (!active) return;
                if (!Array.isArray(data)) {
                    setPlugin(null);
                    return;
                }
                const found = data.find((entry) => entry.id === pluginId) || false;
                setPlugin(found);
                if (!found) return;
                loadEnabled();
                loadAvailable();
            }),
        );
        return () => {
            active = false;
        };
    }, [dispatch, pluginId, loadEnabled, loadAvailable]);

    const updateAvailable = (updater) => {
        if (!Array.isArray(available)) {
            loadAvailable();
            return;
        }
        availableRequestRef.current += 1;
        setAvailable((prev) => (Array.isArray(prev) ? updater(prev) : prev));
    };

    const handleSaved = (data, successMessage, updater) => {
        if (!activeRef.current) return;
        setSaving(false);
        if (!Array.isArray(data)) {
            loadEnabled();
            loadAvailable();
            return;
        }
        enabledRequestRef.current += 1;
        setEnabled(data);
        updateAvailable(updater);
        dispatch(showAlert({ severity: "success", message: successMessage }));
    };

    const handleAdd = () => {
        if (!picked) return;
        const client = picked;
        setSaving(true);
        dispatch(
            enablePluginClient(pluginId, client._id, (data) => {
                if (activeRef.current) setPicked(null);
                handleSaved(data, `${plugin.name} turned on for ${client.name}`, (list) => list.filter((entry) => entry._id !== client._id));
            }),
        );
    };

    const handleRemove = (client) => {
        setSaving(true);
        dispatch(
            disablePluginClient(pluginId, client._id, (data) =>
                handleSaved(data, `${plugin.name} turned off for ${client.name}`, (list) => {
                    if (client.isDeleted || list.some((entry) => entry._id === client._id)) return list;
                    return [...list, { _id: client._id, name: client.name }].sort(compareNames);
                }),
            ),
        );
    };

    const backButton = (
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/plugins")}>
            Back to Plugins
        </Button>
    );

    if (plugin === undefined) {
        return (
            <AdminPageContainer>
                <PageHeader title="Plugin" actions={backButton} />
                <LoadingRow>
                    <CircularProgress />
                </LoadingRow>
            </AdminPageContainer>
        );
    }

    if (!plugin) {
        return (
            <AdminPageContainer>
                <PageHeader title="Plugin" actions={backButton} />
                <EmptyState>
                    <Typography color={plugin === null ? "error" : "text.secondary"}>
                        {plugin === null ? "This plugin could not be loaded. Refresh the page to try again." : "This plugin is not installed."}
                    </Typography>
                </EmptyState>
            </AdminPageContainer>
        );
    }

    return (
        <AdminPageContainer>
            <PageHeader title={plugin.name} subtitle="Choose which clients can use this plugin. Changes save as soon as you make them." actions={backButton} />

            <StaticCard>
                <SectionTitle variant="h6">Enabled for clients</SectionTitle>
                <SectionHelper variant="body2" color="text.secondary">
                    People at these clients see {plugin.name} in their menu.
                </SectionHelper>
                {enabled === undefined ? (
                    <LoadingRow>
                        <CircularProgress size={24} />
                    </LoadingRow>
                ) : enabled === null ? (
                    <Typography color="error">The client list could not be loaded. Refresh the page to try again.</Typography>
                ) : (
                    <>
                        {enabled.length === 0 ? (
                            <SectionHelper color="text.secondary">No clients have this plugin yet.</SectionHelper>
                        ) : (
                            <EnabledList disablePadding>
                                {enabled.map((client) => (
                                    <ListItem
                                        key={client._id}
                                        secondaryAction={
                                            <Tooltip title={`Turn off for ${client.name}`}>
                                                <span>
                                                    <IconButton edge="end" aria-label={`Turn off for ${client.name}`} onClick={() => handleRemove(client)} disabled={saving}>
                                                        <CloseIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        }
                                    >
                                        <ListItemText
                                            primary={
                                                <ClientName>
                                                    {client.name}
                                                    {client.isDeleted && <Chip size="small" label="Deleted" />}
                                                </ClientName>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </EnabledList>
                        )}
                        <AddRow>
                            <ClientPicker
                                options={available || []}
                                loading={available === undefined}
                                value={picked}
                                onChange={(_, client) => setPicked(client)}
                                getOptionLabel={(client) => client?.name || ""}
                                isOptionEqualToValue={(option, selected) => option._id === selected._id}
                                renderOption={(props, option) => (
                                    <li {...props} key={option._id}>
                                        {option.name}
                                    </li>
                                )}
                                noOptionsText={available === null ? "Clients could not be loaded" : "No more clients to add"}
                                autoHighlight
                                disabled={saving}
                                renderInput={(params) => <TextField {...params} label="Add a client" placeholder="Search clients" />}
                            />
                            <AddButton variant="contained" onClick={handleAdd} disabled={!picked || saving}>
                                {saving ? "Saving..." : "Turn on"}
                            </AddButton>
                        </AddRow>
                    </>
                )}
            </StaticCard>

            {plugin.hasAdmin && manifest?.admin && (
                <StaticCard>
                    <SectionTitle variant="h6">Settings</SectionTitle>
                    <SectionHelper variant="body2" color="text.secondary">
                        These settings apply to every client that has {plugin.name}.
                    </SectionHelper>
                    <Suspense fallback={<CircularProgress size={24} />}>{manifest.admin}</Suspense>
                </StaticCard>
            )}
        </AdminPageContainer>
    );
};

const AdminPlugins = () => {
    const { pluginId } = useParams();
    return pluginId ? <PluginDetail key={pluginId} pluginId={pluginId} /> : <PluginList />;
};

export default AdminPlugins;
